import axios, { AxiosError } from 'axios'
import toast from 'react-hot-toast'
import { sessionValidationService } from './sessionValidationService'

// Phase 34 Task 3.1: Pure cookie-based authentication - no client-side token storage
// Phase 34 Task 3.2: Pure cookie-based session management - no client-side session storage needed

// Ensure cookies are always sent with requests
axios.defaults.withCredentials = true

// ============================================================================
// Error Normalization (shared by all domain modules)
// ============================================================================

export const normalizeError = (value: unknown): string => {
  if (value === null || value === undefined) {
    return 'Unexpected error'
  }

  if (value instanceof Error) {
    return value.message
  }

  if (typeof value === 'string') {
    return value
  }

  if (Array.isArray(value)) {
    return value.map((item) => normalizeError(item)).join(', ')
  }

  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>
    if (obj.detail !== undefined) {
      return normalizeError(obj.detail)
    }
    if (obj.error !== undefined) {
      return normalizeError(obj.error)
    }
    try {
      return JSON.stringify(value)
    } catch {
      return String(value)
    }
  }

  return String(value)
}

// ============================================================================
// Base URL Configuration
// ============================================================================
// W2/BUG-121-C1 (Phase 121, 2026-07-09): `authBaseURL` removed. The SPA must never
// call a backend service directly (BR-3 two-layer gateway) — everything goes
// through the BFF. Enforced by src/tests/gatewayInvariant.test.ts.

// Use Vite proxy in dev to keep requests same-origin; resolve to absolute in prod
export const bffBaseURL = (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.DEV)
  ? ''
  : ((typeof window !== 'undefined' && (window as any).__APP_CONFIG__ && (window as any).__APP_CONFIG__.apiBaseUrl)
    || (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_API_URL)
    || '')

// ============================================================================
// CSRF Token Management
// ============================================================================

let csrfToken: string | null = null

export const getCsrfToken = (): string | null => csrfToken
export const setCsrfToken = (token: string | null): void => { csrfToken = token }

// ============================================================================
// Interceptors Setup
// ============================================================================

function setupInterceptors(): void {
  // Request interceptor for pure cookie-based authentication (Phase 34 Task 3.1)
  axios.interceptors.request.use(
    config => {
      config.withCredentials = true
      if (config.headers && config.headers['Authorization']) {
        delete config.headers['Authorization']
      }

      if (csrfToken && config.method && config.method.toUpperCase() !== 'GET') {
        config.headers = config.headers || {}
        config.headers['X-CSRF-Token'] = csrfToken
      }

      return config
    },
    error => Promise.reject(error)
  )

  // Response interceptor for pure cookie-based authentication (Phase 34 Task 3.2)
  // Phase 63: Session timeout now handled by ActivityMonitor heartbeat mechanism
  axios.interceptors.response.use(
    response => {
      return response
    },
    async (error) => {
      // Enhanced error handling for cookie-based authentication
      if (error.response?.status === 401) {
        console.log('401 error detected - session expired or invalid')

        // Check if this is a session validation or login request to avoid infinite loops
        if (error.config?.url?.includes('/session/validate')) {
          console.log('Session validation failed - user needs to login')
          return Promise.reject(error)
        }

        // A 401 on login means bad credentials, not an expired session.
        // Let the authApi.login() catch handler display the inline error.
        if (error.config?.url?.includes('/session/login')) {
          return Promise.reject(error)
        }

        // For other 401 errors, try to validate session once before logout
        // Inline validation call to avoid circular dependency with authApi
        try {
          const response = await axios.get(`${bffBaseURL}/api/session/validate`, {
            skipTokenRefresh: true,
            withCredentials: true
          } as any)

          // Update CSRF token if present
          const csrfHeader = response.headers['x-csrf-token'] || response.headers['X-CSRF-Token']
          if (csrfHeader) {
            csrfToken = csrfHeader
          }

          if (response.data?.valid) {
            console.log('Session is still valid, retrying original request')
            return axios.request(error.config)
          }
        } catch {
          console.log('Session validation failed during 401 handling')
        }

        // Session is invalid, redirect to login
        // Phase 63 BUG-003 Fix: Call handleSessionExpired() for proper redirect
        console.log('Session invalid, redirecting to login')
        sessionValidationService.handleSessionExpired()
        return Promise.reject(error)
      }

      // Handle other authentication-related errors
      if (error.response?.status === 403) {
        console.log('403 error detected - insufficient permissions')
        toast.error('Insufficient permissions for this action')
      }

      // Handle network errors that might indicate server issues
      if (!error.response && error.code === 'NETWORK_ERROR') {
        console.log('Network error detected - server may be unavailable')
        toast.error('Unable to connect to server. Please check your connection.')
      }

      return Promise.reject(error)
    }
  )
}

// Initialize interceptors on module load
setupInterceptors()

// ============================================================================
// Generic HTTP Methods (backward compat — used by useUserOperations)
// ============================================================================
// Phase 76 M5 Task 5.2: Removed validateAdminSession() function.
// Pre-flight admin validation was redundant with BFF gate() enforcement.
// See Decision D14 in phase_plan.md.
// ============================================================================

export async function httpGet(url: string) {
  try {
    const response = await axios.get(url.includes('localhost:') ? url : `${bffBaseURL}${url}`)
    return response
  } catch (error) {
    throw error
  }
}

export async function httpPost(url: string, data?: any) {
  try {
    const response = await axios.post(url.includes('localhost:') ? url : `${bffBaseURL}${url}`, data)
    return response
  } catch (error) {
    throw error
  }
}

export async function httpPut(url: string, data?: any) {
  try {
    const response = await axios.put(url.includes('localhost:') ? url : `${bffBaseURL}${url}`, data)
    return response
  } catch (error) {
    throw error
  }
}

export async function httpDelete(url: string) {
  try {
    const response = await axios.delete(url.includes('localhost:') ? url : `${bffBaseURL}${url}`)
    return response
  } catch (error) {
    throw error
  }
}
